// Add / Edit Software Licence. The stored licence key is never sent to the
// browser: on edit the key field starts empty and is only submitted when
// the user types a new key (or ticks "clear").
import React, { useEffect, useState } from "react";
import api from "../../api";
import { Modal, Button } from "../../components/ui";
import { LICENSE_TYPES, apiError } from "./licenseFormat";

const EMPTY = {
  software_name: "",
  vendor_name: "",
  license_type: "SUBSCRIPTION",
  license_key: "",
  purchase_reference: "",
  purchase_date: "",
  start_date: "",
  expiry_date: "",
  purchase_amount: "",
  total_seats: "1",
  unit_id: "",
  status: "ACTIVE",
  remarks: "",
};

export default function LicenseFormModal({ show, onClose, onSaved, licence, meta }) {
  const isEdit = !!licence;
  const [form, setForm] = useState(EMPTY);
  const [clearKey, setClearKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    setError("");
    setClearKey(false);
    setForm(
      licence
        ? {
            ...EMPTY,
            ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, licence[k] ?? ""])),
            license_key: "",
            purchase_amount: String(licence.purchase_amount ?? ""),
            total_seats: String(licence.total_seats ?? "1"),
            unit_id: String(licence.unit_id ?? ""),
          }
        : { ...EMPTY, unit_id: meta?.can_choose_unit ? "" : String(meta?.own_unit_id ?? "") }
    );
  }, [show, licence, meta]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const perpetual = form.license_type === "PERPETUAL";
  const expiryRequired = form.license_type === "SUBSCRIPTION";

  const submit = async () => {
    if (!form.software_name.trim()) return setError("Software Name is required.");
    if (meta?.can_choose_unit && !form.unit_id) return setError("Unit is required.");
    if (expiryRequired && !form.expiry_date) return setError("Expiry Date is required for a Subscription licence.");
    if (form.start_date && form.expiry_date && form.expiry_date < form.start_date) {
      return setError("Expiry Date cannot be before the Start Date.");
    }
    const seats = Number(form.total_seats);
    if (!Number.isInteger(seats) || seats < 1) return setError("Number of Licences / Seats must be a whole number of at least 1.");
    if (form.purchase_amount !== "" && (!Number.isFinite(Number(form.purchase_amount)) || Number(form.purchase_amount) < 0)) {
      return setError("Purchase Amount must be zero or a positive number.");
    }

    const { license_key: key, unit_id: unitId, ...rest } = form;
    const payload = { ...rest, total_seats: seats };
    if (unitId) payload.unit_id = Number(unitId);
    if (!isEdit && key.trim()) payload.license_key = key.trim();
    if (isEdit && (key.trim() || clearKey)) payload.license_key = clearKey ? null : key.trim();

    setSaving(true);
    setError("");
    try {
      const res = isEdit
        ? await api.put(`/software-licenses/${licence.id}`, payload)
        : await api.post("/software-licenses", payload);
      onSaved(res.data?.data);
    } catch (err) {
      setError(apiError(err, "Failed to save the licence."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={saving ? undefined : onClose}
      size="lg"
      title={isEdit ? "Edit Software Licence" : "Add Software Licence"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} loading={saving}>{isEdit ? "Save Changes" : "Add Licence"}</Button>
        </>
      }
    >
      {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
      <div className="sl-form-grid">
        <label className="sl-field sl-span-2">
          <span className="form-label">Software Name *</span>
          <input className="form-control" value={form.software_name} onChange={set("software_name")} maxLength={150} />
        </label>
        <label className="sl-field">
          <span className="form-label">Vendor / Company</span>
          <input className="form-control" list="sl-vendors" value={form.vendor_name} onChange={set("vendor_name")} maxLength={150} />
          <datalist id="sl-vendors">{(meta?.vendors || []).map((v) => <option key={v} value={v} />)}</datalist>
        </label>
        <label className="sl-field">
          <span className="form-label">Licence Type *</span>
          <select className="form-select" value={form.license_type} onChange={set("license_type")}>
            {LICENSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>

        <label className="sl-field">
          <span className="form-label">Unit {meta?.can_choose_unit ? "*" : ""}</span>
          <select className="form-select" value={form.unit_id} onChange={set("unit_id")} disabled={!meta?.can_choose_unit}>
            {meta?.can_choose_unit && <option value="">Select unit</option>}
            {(meta?.units || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
        <label className="sl-field">
          <span className="form-label">Status</span>
          <select className="form-select" value={form.status} onChange={set("status")}>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </label>

        <label className="sl-field sl-span-2">
          <span className="form-label">Licence Key / Reference</span>
          <input
            className="form-control"
            type="password"
            autoComplete="off"
            value={form.license_key}
            onChange={set("license_key")}
            disabled={clearKey}
            placeholder={isEdit && licence?.has_license_key ? `Stored: ${licence.license_key_masked} (leave blank to keep)` : ""}
            maxLength={1000}
          />
          {isEdit && licence?.has_license_key && (
            <span className="form-check mt-1">
              <input id="sl-clear-key" type="checkbox" className="form-check-input" checked={clearKey} onChange={(e) => setClearKey(e.target.checked)} />
              <label htmlFor="sl-clear-key" className="form-check-label small">Remove the stored key</label>
            </span>
          )}
        </label>

        <label className="sl-field">
          <span className="form-label">Purchase Date</span>
          <input className="form-control" type="date" value={form.purchase_date} onChange={set("purchase_date")} />
        </label>
        <label className="sl-field">
          <span className="form-label">Invoice No. / Purchase Reference</span>
          <input className="form-control" value={form.purchase_reference} onChange={set("purchase_reference")} maxLength={100} />
        </label>
        <label className="sl-field">
          <span className="form-label">Start Date</span>
          <input className="form-control" type="date" value={form.start_date} onChange={set("start_date")} />
        </label>
        <label className="sl-field">
          <span className="form-label">Expiry Date {expiryRequired ? "*" : ""}</span>
          <input className="form-control" type="date" value={form.expiry_date} onChange={set("expiry_date")} />
          {perpetual && <small className="text-muted">Optional — a perpetual licence never expires.</small>}
        </label>
        <label className="sl-field">
          <span className="form-label">Purchase Amount (₹)</span>
          <input className="form-control" type="number" min="0" step="0.01" value={form.purchase_amount} onChange={set("purchase_amount")} />
        </label>
        <label className="sl-field">
          <span className="form-label">Number of Licences / Seats *</span>
          <input className="form-control" type="number" min={Math.max(1, licence?.assigned_seats || 0)} step="1" value={form.total_seats} onChange={set("total_seats")} />
          {isEdit && licence?.assigned_seats > 0 && <small className="text-muted">{licence.assigned_seats} seat(s) currently assigned.</small>}
        </label>

        <label className="sl-field sl-span-2">
          <span className="form-label">Remarks</span>
          <textarea className="form-control" rows={2} value={form.remarks} onChange={set("remarks")} maxLength={2000} />
        </label>
      </div>
    </Modal>
  );
}
